import {
  type SanityProductFragment,
  type SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { getPrice } from '@lib/format'
import { getSanityImageUrl } from '@lib/image'
import { type Locale } from '@lib/language'
import { getProductGalleryPhotos } from '@lib/product/images'
import { getPageUrl } from '@lib/routes'
import { getPortableTextHtml } from './portable-text'
import { type ProductSchema } from './types'

/**
 * Gets the first existing value from an array of values.
 */
function getFirstValue<T = string>(values: (T | undefined)[]): T | undefined {
  return values.filter(Boolean)[0]
}

/**
 * Returns product schema.
 */
export const getProductSchema = (
  locale: Locale,
  product?: SanityProductFragment,
  variant?: SanityProductVariantFragment,
  siteTitle?: string,
  siteUrl?: string,
  currencyCode?: string,
  taxRate?: number,
  taxesIncluded?: boolean
) => {
  if (
    typeof product === 'undefined' ||
    typeof currencyCode === 'undefined' ||
    typeof taxRate === 'undefined' ||
    typeof taxesIncluded === 'undefined'
  ) {
    return
  }

  const description = getPortableTextHtml(product.description)
  const isInStock = variant?.inStock ?? product.inStock
  const price = getPrice(variant?.price ?? product.price ?? 0, {
    taxRate,
    applyTax: !taxesIncluded,
  })
  const comparePrice = getPrice(
    variant?.comparePrice ?? product.comparePrice ?? 0,
    {
      taxRate,
      applyTax: !taxesIncluded,
    }
  )
  const domain = siteUrl?.replace(/\/+$/g, '')
  const productUrl = getPageUrl({
    locale,
    pageType: 'product',
    slugs: product.slug.current,
    parameters: {
      ...(variant
        ? {
            variant: `${variant?.variantID}`,
          }
        : {}),
    },
  })
  const nextYearIsoDate = new Date(
    new Date().setFullYear(new Date().getFullYear() + 1)
  )
    .toISOString()
    .split('T')[0]

  const getImage = () => {
    const photos = product.galleryPhotos
      ? getProductGalleryPhotos(product.galleryPhotos, variant)
      : []

    if (photos.length === 0) {
      return
    }

    return getSanityImageUrl(photos[0], {
      width: 800,
      fitMax: true,
      autoFormat: true,
    })
  }

  const productSchema: ProductSchema = {
    '@type': 'Product',
    name: product.title,
    description,
    sku: getFirstValue([variant?.sku, product.sku]),
    gtin: getFirstValue([variant?.barcode, product.barcode]),
    brand: {
      '@type': 'Brand',
      name: siteTitle ?? '',
    },
    image: getImage(),
    offers: {
      '@type': 'Offer',
      availability: isInStock
        ? 'http://schema.org/InStock'
        : 'http://schema.org/SoldOut',
      price: comparePrice && Number(comparePrice) > 0 ? comparePrice : price,
      priceCurrency: currencyCode,
      url: `${domain}${productUrl}`,
      priceValidUntil: nextYearIsoDate,
    },
  }

  return JSON.stringify(productSchema)
}
